import React, { useState, useEffect, useRef, useContext } from 'react'
import {
  X,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  FileBadge,
  Link,
} from 'lucide-react'
import { SteelOrder } from '../../SteelOrdersTable/types'
import duplicate from '../../../../../assets/fashion/Actions/duplicate.svg'

import {
  Overlay,
  Container,
  Header,
  CloseBtn,
  Content,
  Card,
  CardHeader,
  CardInfo,
  CardActions,
  ActionWrapper,
  OutlineButton,
  DropdownMenu,
  DropdownItem,
  IconButton,
  TimelineContainer,
  TimelineItem,
  Dot,
  StepContent,
  StepName,
  StepDate,
  Footer,
  FooterButton,
  LinkButton,
} from './style'
import ApiService from '../../../../../services/ApiService'
import UserContext from '../../../../../contexts/UserContext'
import { FeedbackContext } from '../../../../../contexts/FeedbackContext'

interface ModalSteelOrderProps {
  isOpen: boolean
  onClose: () => void
  order: SteelOrder | null
}

const OPCard = ({ item }: { item: any }) => {
  const [isOpen, setIsOpen] = useState(true)
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // === LÓGICA DINÂMICA ===
  // Pega as etapas que vieram do banco (service_stage)
  const stages = item.stages || []

  // Descobre qual é a etapa ATUAL (A última completada OU a primeira não completada)
  // Se quisermos mostrar o nome da etapa que está SENDO FEITA agora:
  // Pegamos a primeira etapa onde completed === false.
  const currentStageObj =
    stages.find((s: any) => !s.completed) || stages[stages.length - 1]

  const currentStageName = currentStageObj
    ? currentStageObj.name
    : 'Aguardando / Finalizado'

  // Data global do serviço (já que service_stage não tem data individual no schema)
  const formattedDate = item.lastUpdate
    ? new Date(item.lastUpdate).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''

  return (
    <Card>
      <CardHeader>
        <CardInfo>
          <div>
            Ordem de produção:{' '}
            <span className='blue-text'>{item.opCode || `OP-${item.id}`}</span>
          </div>
          <div className='item-name' title={item.name}>
            Item: {item.name || 'Item sem descrição'}
          </div>
          <div>
            Etapa Atual: <strong>{currentStageName}</strong>
          </div>
        </CardInfo>

        <CardActions>
          <ActionWrapper ref={dropdownRef}>
            <OutlineButton onClick={() => setShowDropdown(!showDropdown)}>
              <Download size={16} />
              Baixar arquivos
            </OutlineButton>

            {showDropdown && (
              <DropdownMenu>
                <DropdownItem onClick={() => console.log('Baixar Certificado')}>
                  <FileBadge size={18} />
                  Certificado
                </DropdownItem>
                <DropdownItem onClick={() => console.log('Baixar Nota Fiscal')}>
                  <FileText size={18} />
                  Nota fiscal
                </DropdownItem>
              </DropdownMenu>
            )}
          </ActionWrapper>

          <IconButton onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </IconButton>
        </CardActions>
      </CardHeader>

      {isOpen && (
        <TimelineContainer>
          {stages.length === 0 ? (
            <div
              style={{
                padding: '10px',
                color: '#999',
                fontSize: '13px',
                fontStyle: 'italic',
              }}
            >
              Nenhuma etapa definida para esta OP.
            </div>
          ) : (
            stages.map((stage: any, index: number) => {
              // Usa o boolean 'completed' que vem do banco
              const isCompleted = stage.completed === true

              // Se é a última da lista
              const isLastItem = index === stages.length - 1

              // Lógica visual:
              // Se completed = true -> Azul
              // Se completed = false -> Cinza

              return (
                <TimelineItem
                  key={stage.id || index}
                  $isActive={isCompleted}
                  $isLast={isLastItem}
                >
                  <Dot $isActive={isCompleted} />
                  <StepContent>
                    {/* Nome fica em Destaque/Azul se estiver completo */}
                    <StepName $isCurrent={isCompleted}>{stage.name}</StepName>

                    {/* Mostra a data se for a última etapa concluída (Status atual) */}
                    {isCompleted &&
                      index ===
                        stages.filter((s: any) => s.completed).length - 1 && (
                        <StepDate $isCurrent={true}>{formattedDate}</StepDate>
                      )}
                  </StepContent>
                </TimelineItem>
              )
            })
          )}
        </TimelineContainer>
      )}
    </Card>
  )
}

const ModalSteelOrder: React.FC<ModalSteelOrderProps> = ({
  isOpen,
  onClose,
  order,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { user } = useContext(UserContext)
  const { setFeedback } = useContext(FeedbackContext)
  
  const isTenantClient = (user?.role as string) === 'TENANT_CLIENT';

  if (!isOpen || !order) return null

  const handleGenerateLink = async () => {
    setIsGenerating(true);
    try {
      const payload = {
        companyId: (order as any).clientId, 
        cnpj: (order as any).clientCnpj || "", 
        name: (order as any).client || "Cliente", 
        email: (order as any).clientEmail || "", 
        company: (order as any).client || "", 
        orders: [order.id], 
        approved: "approved",
        adminUser: false,
        role: "TENANT_CLIENT",
        permissions: ['order']
      };

      const response = await ApiService.user.generateLink(payload);

      if (response.data && response.data.link) {
        await navigator.clipboard.writeText(response.data.link);
        onClose(); 
        setFeedback({
          isOpen: true,
          message: 'Link gerado e copiado para a área de transferência!',
          statusOk: true,
        });
      }
      
    } catch (error) {
      console.error("Erro ao chamar link-generator:", error);
      onClose();
      setFeedback({
        isOpen: true,
        message: 'Erro ao gerar o link. Tente novamente.',
        statusOk: false,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const items = (order as any).items || []

  return (
    <Overlay onClick={onClose}>
      <Container onClick={(e) => e.stopPropagation()}>
        <Header>
          <h2>
            Pedido {order.id} <span>{items.length} itens</span>
          </h2>
          <CloseBtn onClick={onClose}>
            <X size={20} />
          </CloseBtn>
        </Header>

        <Content>
          {items.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#888',
                gap: '10px',
                marginTop: '40px',
              }}
            >
              <p>Nenhum item de produção encontrado neste pedido.</p>
              <small style={{ fontSize: '12px', color: '#AAA' }}>
                (Dados vindos do servidor: {items.length})
              </small>
            </div>
          ) : (
            items.map((item: any, index: number) => (
              <OPCard key={item.id || index} item={item} />
            ))
          )}
        </Content>

        <Footer>
          {!isTenantClient && (
            <LinkButton onClick={handleGenerateLink}> <Link size={16} color='white'/> {isGenerating ? 'Gerando...' : 'Gerar link'}</LinkButton>
          )}
          <FooterButton onClick={onClose}>Fechar</FooterButton>
        </Footer>
      </Container>
    </Overlay>
  )
}

export default ModalSteelOrder
